import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { useState, createContext, useContext } from "react";

// Profile selector (multi-role landing)
import ProfileSelectorPage from "@/pages/ProfileSelectorPage";

// Telemedicine app pages (Operatore Farmacia)
import LoginPage from "@/pages/LoginPage";
import PatientSelectionPage from "@/pages/PatientSelectionPage";
import ServicesPage from "@/pages/ServicesPage";
import DeviceConnectionPage from "@/pages/DeviceConnectionPage";
import ResultsPage from "@/pages/ResultsPage";
import ReportingRoutingPage from "@/pages/ReportingRoutingPage";
import ExportPage from "@/pages/ExportPage";

// Pharmacy dashboard (operator/manager shared analytics)
import DashboardLayout from "@/pages/dashboard/DashboardLayout";
import DashboardHome from "@/pages/dashboard/DashboardHome";
import CalendarPage from "@/pages/dashboard/CalendarPage";
import PatientsListPage from "@/pages/dashboard/PatientsListPage";
import PatientDetailPage from "@/pages/dashboard/PatientDetailPage";
import AnalyticsPage from "@/pages/dashboard/AnalyticsPage";

// Patient portal (Cittadino)
import PatientPortalLayout from "@/pages/patient/PatientPortalLayout";
import PatientLoginPage from "@/pages/patient/PatientLoginPage";
import PatientHomePage from "@/pages/patient/PatientHomePage";
import PatientHistoryPage from "@/pages/patient/PatientHistoryPage";
import PatientProfilePage from "@/pages/patient/PatientProfilePage";
import PatientDevicesPage from "@/pages/patient/PatientDevicesPage";
import PatientWearableDataPage from "@/pages/patient/PatientWearableDataPage";
import PatientBookingsPage from "@/pages/patient/PatientBookingsPage";
import PatientPharmaciesPage from "@/pages/patient/PatientPharmaciesPage";
import PatientConsentsPage from "@/pages/patient/PatientConsentsPage";

// Doctor portal (Medico Specialista)
import DoctorPortalLayout from "@/pages/doctor/DoctorPortalLayout";
import DoctorLoginPage from "@/pages/doctor/DoctorLoginPage";
import DoctorDashboard from "@/pages/doctor/DoctorDashboard";
import DoctorPendingReports from "@/pages/doctor/DoctorPendingReports";
import DoctorCompletedReports from "@/pages/doctor/DoctorCompletedReports";
import DoctorReportDetailPage from "@/pages/doctor/DoctorReportDetailPage";

// Pharmacy Manager portal (Responsabile Farmacia)
import ManagerLayout from "@/pages/manager/ManagerLayout";
import ManagerHomePage from "@/pages/manager/ManagerHomePage";
import ManagerPlanningPage from "@/pages/manager/ManagerPlanningPage";
import ManagerCampaignsPage from "@/pages/manager/ManagerCampaignsPage";
import ManagerMonitoringPage from "@/pages/manager/ManagerMonitoringPage";
import ManagerInventoryPage from "@/pages/manager/ManagerInventoryPage";
import ManagerConfigurationPage from "@/pages/manager/ManagerConfigurationPage";
import ManagerBookingsPage from "@/pages/manager/ManagerBookingsPage";

// Accredited Structure portal (Responsabile Struttura)
import StructureLayout from "@/pages/structure/StructureLayout";
import StructureHomePage from "@/pages/structure/StructureHomePage";
import StructureSpecialistsPage from "@/pages/structure/StructureSpecialistsPage";
import StructureMonitoringPage from "@/pages/structure/StructureMonitoringPage";
import StructureBillingPage from "@/pages/structure/StructureBillingPage";
import StructureConfigurationPage from "@/pages/structure/StructureConfigurationPage";

// System Admin portal (Amministratore)
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminHomePage from "@/pages/admin/AdminHomePage";
import AdminExamTypesPage from "@/pages/admin/AdminExamTypesPage";
import AdminServicesPage from "@/pages/admin/AdminServicesPage";
import AdminBundlesPage from "@/pages/admin/AdminBundlesPage";
import AdminPDTAPage from "@/pages/admin/AdminPDTAPage";
import AdminUsersRegistryPage from "@/pages/admin/AdminUsersRegistryPage";
import AdminPharmaciesPage from "@/pages/admin/AdminPharmaciesPage";
import AdminStructuresPage from "@/pages/admin/AdminStructuresPage";
import AdminDevicesPage from "@/pages/admin/AdminDevicesPage";
import AdminUsersPage from "@/pages/admin/AdminUsersPage";
import AdminMonitoringPage from "@/pages/admin/AdminMonitoringPage";

// Booking flow (shared across roles)
import BookingPage from "@/pages/booking/BookingPage";

// Create App Context for global state
export const AppContext = createContext(null);

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within AppProvider");
  }
  return context;
};

function App() {
  // Authentication
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  // Active role selected on the profile selector
  const [activeRole, setActiveRole] = useState(null);
  // Telemedicine flow state
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedServices, setSelectedServices] = useState([]);
  const [deviceReadings, setDeviceReadings] = useState({});
  const [sessionResults, setSessionResults] = useState(null);

  const contextValue = {
    isAuthenticated,
    setIsAuthenticated,
    currentUser,
    setCurrentUser,
    activeRole,
    setActiveRole,
    selectedPatient,
    setSelectedPatient,
    selectedServices,
    setSelectedServices,
    deviceReadings,
    setDeviceReadings,
    sessionResults,
    setSessionResults,
    resetSession: () => {
      setSelectedPatient(null);
      setSelectedServices([]);
      setDeviceReadings({});
      setSessionResults(null);
    },
    logout: () => {
      setIsAuthenticated(false);
      setCurrentUser(null);
      setActiveRole(null);
      setSelectedPatient(null);
      setSelectedServices([]);
      setDeviceReadings({});
      setSessionResults(null);
    }
  };

  return (
    <AppContext.Provider value={contextValue}>
      <div className="min-h-screen bg-background">
        <BrowserRouter>
          <Routes>
            {/* ========== ROOT: PROFILE SELECTOR ========== */}
            <Route path="/" element={isAuthenticated ? <ProfileSelectorPage /> : <Navigate to="/login" replace />} />

            {/* ========== AUTHENTICATION ========== */}
            <Route path="/login" element={<LoginPage />} />

            {/* ========== BOOKING FLOW (shared) ========== */}
            <Route
              path="/booking"
              element={isAuthenticated ? <BookingPage /> : <Navigate to="/login" replace />}
            />

            {/* ========== TELEMEDICINE APP (Operatore Farmacia) ========== */}
            <Route
              path="/patients"
              element={isAuthenticated ? <PatientSelectionPage /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/services"
              element={isAuthenticated && selectedPatient ? <ServicesPage /> : <Navigate to="/patients" replace />}
            />
            <Route
              path="/devices"
              element={isAuthenticated && selectedServices.length > 0 ? <DeviceConnectionPage /> : <Navigate to="/services" replace />}
            />
            <Route
              path="/results"
              element={isAuthenticated && Object.keys(deviceReadings).length > 0 ? <ResultsPage /> : <Navigate to="/devices" replace />}
            />
            <Route
              path="/reporting-routing"
              element={isAuthenticated && sessionResults ? <ReportingRoutingPage /> : <Navigate to="/results" replace />}
            />
            <Route
              path="/export"
              element={isAuthenticated && sessionResults ? <ExportPage /> : <Navigate to="/results" replace />}
            />

            {/* ========== PHARMACY DASHBOARD (operator analytics) ========== */}
            <Route path="/dashboard" element={<DashboardLayout />}>
              <Route index element={<DashboardHome />} />
              <Route path="calendar" element={<CalendarPage />} />
              <Route path="patients" element={<PatientsListPage />} />
              <Route path="patients/:patientId" element={<PatientDetailPage />} />
              <Route path="analytics" element={<AnalyticsPage />} />
            </Route>

            {/* ========== PHARMACY MANAGER (Responsabile Farmacia) ========== */}
            <Route path="/manager" element={<ManagerLayout />}>
              <Route index element={<ManagerHomePage />} />
              <Route path="planning" element={<ManagerPlanningPage />} />
              <Route path="campaigns" element={<ManagerCampaignsPage />} />
              <Route path="monitoring" element={<ManagerMonitoringPage />} />
              <Route path="inventory" element={<ManagerInventoryPage />} />
              <Route path="configuration" element={<ManagerConfigurationPage />} />
              <Route path="bookings" element={<ManagerBookingsPage />} />
            </Route>

            {/* ========== ACCREDITED STRUCTURE (Responsabile Struttura) ========== */}
            <Route path="/structure" element={<StructureLayout />}>
              <Route index element={<StructureHomePage />} />
              <Route path="specialists" element={<StructureSpecialistsPage />} />
              <Route path="monitoring" element={<StructureMonitoringPage />} />
              <Route path="billing" element={<StructureBillingPage />} />
              <Route path="configuration" element={<StructureConfigurationPage />} />
            </Route>

            {/* ========== SYSTEM ADMIN (Amministratore) ========== */}
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminHomePage />} />
              <Route path="pharmacies" element={<AdminPharmaciesPage />} />
              <Route path="structures" element={<AdminStructuresPage />} />
              <Route path="devices" element={<AdminDevicesPage />} />
              <Route path="users" element={<AdminUsersPage />} />
              <Route path="monitoring" element={<AdminMonitoringPage />} />
              <Route path="config/exam-types" element={<AdminExamTypesPage />} />
              <Route path="config/services" element={<AdminServicesPage />} />
              <Route path="config/bundles" element={<AdminBundlesPage />} />
              <Route path="config/pdta" element={<AdminPDTAPage />} />
              <Route path="config/users" element={<AdminUsersRegistryPage />} />
            </Route>

            {/* ========== PATIENT PORTAL (Cittadino) ========== */}
            <Route path="/patient/login" element={<PatientLoginPage />} />
            <Route path="/patient" element={<PatientPortalLayout />}>
              <Route index element={<PatientHomePage />} />
              <Route path="bookings" element={<PatientBookingsPage />} />
              <Route path="history" element={<PatientHistoryPage />} />
              <Route path="devices" element={<PatientDevicesPage />} />
              <Route path="devices/callback" element={<PatientDevicesPage />} />
              <Route path="health-data" element={<PatientWearableDataPage />} />
              <Route path="pharmacies" element={<PatientPharmaciesPage />} />
              <Route path="consents" element={<PatientConsentsPage />} />
              <Route path="profile" element={<PatientProfilePage />} />
            </Route>

            {/* ========== DOCTOR PORTAL (Medico Specialista) ========== */}
            <Route path="/doctor/login" element={<DoctorLoginPage />} />
            <Route path="/doctor" element={<DoctorPortalLayout />}>
              <Route index element={<DoctorDashboard />} />
              <Route path="pending" element={<DoctorPendingReports />} />
              <Route path="completed" element={<DoctorCompletedReports />} />
              <Route path="report/:reportId" element={<DoctorReportDetailPage />} />
            </Route>

            {/* Default redirect */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-center" richColors />
      </div>
    </AppContext.Provider>
  );
}

export default App;
